import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { IContest, TContestRefundPolicy } from '../../../types/global/helper';
import {
    DrawerSectionEditorActions,
    editTriggerSx,
    sectionShellSx,
} from './BrandContestDetailsDrawerUi';
import { dashedFieldShellSx, FieldHint } from './brandContestDrawerFieldUtils';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../contestWizard/midnightStyles';

export const CONTEST_REFUND_POLICY_OPTIONS: TContestRefundPolicy[] = [
    'non-refundable',
    'refundable during submission',
];

export const DEFAULT_CONTEST_REFUND_POLICY: TContestRefundPolicy = 'refundable during submission';

export function resolveContestRefundPolicy(contest: IContest): TContestRefundPolicy {
    return contest.refund_policy ?? DEFAULT_CONTEST_REFUND_POLICY;
}

const LOCKED_MESSAGE = 'Campaign already has submissions. You cannot change the refund policy settings.';

export interface BrandContestRefundPolicyDrawerBodyProps {
    contest: IContest;
    onSaveChanges: (payload: Record<string, unknown>) => void;
    hasSubmissions?: boolean;
    saving?: boolean;
}

export function BrandContestRefundPolicyDrawerBody({
    contest,
    onSaveChanges,
    hasSubmissions = false,
    saving,
}: BrandContestRefundPolicyDrawerBodyProps) {
    const [editing, setEditing] = useState(false);
    const currentPolicy = resolveContestRefundPolicy(contest);
    const [draftPolicy, setDraftPolicy] = useState<TContestRefundPolicy>(currentPolicy);
    const locked = hasSubmissions;

    useEffect(() => {
        if (editing) return;
        setDraftPolicy(resolveContestRefundPolicy(contest));
    }, [contest, editing]);

    const beginEdit = () => {
        if (locked) return;
        setDraftPolicy(resolveContestRefundPolicy(contest));
        setEditing(true);
    };

    const cancelEdit = () => setEditing(false);

    const savePolicy = useCallback(() => {
        if (draftPolicy !== resolveContestRefundPolicy(contest)) {
            onSaveChanges({ refund_policy: draftPolicy });
        }
        setEditing(false);
    }, [contest, draftPolicy, onSaveChanges]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={sectionShellSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                    <Typography sx={{ ...editorialHeadlineSx, fontSize: 20, color: midnight.onSurface }}>
                        Refund policy
                    </Typography>
                    {editing ? (
                        <Box />
                    ) : (
                        <Button
                            type="button"
                            variant="text"
                            size="small"
                            disabled={locked}
                            onClick={beginEdit}
                            sx={editTriggerSx}
                            title={locked ? LOCKED_MESSAGE : undefined}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            Edit
                        </Button>
                    )}
                </Box>
                <FieldHint>Select the refund policy for your campaign.</FieldHint>
                {locked && (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mb: 2, fontStyle: 'italic' }}>
                        {LOCKED_MESSAGE}
                    </Typography>
                )}
                {editing ? (
                    <Box>
                        <Autocomplete
                            disableClearable
                            options={CONTEST_REFUND_POLICY_OPTIONS}
                            value={draftPolicy}
                            onChange={(_, newValue) =>
                                setDraftPolicy((newValue || DEFAULT_CONTEST_REFUND_POLICY) as TContestRefundPolicy)
                            }
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Refund policy"
                                    sx={{
                                        ...editorialInputSx,
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: midnight.surfaceHighest,
                                            borderRadius: '10px',
                                        },
                                    }}
                                />
                            )}
                            slotProps={{
                                paper: {
                                    sx: {
                                        bgcolor: midnight.surfaceContainer,
                                        color: midnight.onSurface,
                                        '& .MuiAutocomplete-option': {
                                            fontSize: 14,
                                        },
                                    },
                                },
                            }}
                        />
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={savePolicy}
                            saving={saving}
                        />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        disabled={locked}
                        onClick={locked ? undefined : beginEdit}
                        sx={{
                            ...dashedFieldShellSx,
                            width: '100%',
                            textAlign: 'left',
                            cursor: locked ? 'not-allowed' : 'pointer',
                            opacity: locked ? 0.6 : 1,
                            border: 'none',
                            bgcolor: 'transparent',
                            p: 0,
                            '&:hover': locked
                                ? {}
                                : {
                                      borderColor: 'rgba(255,201,101,0.45)',
                                      bgcolor: 'rgba(255,255,255,0.03)',
                                  },
                        }}>
                        <Typography sx={{ ...editorialLabelSx, textTransform: 'none', letterSpacing: 0, mb: 0.5 }}>
                            Current policy
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: 15, color: midnight.onSurface }}>
                            {currentPolicy}
                        </Typography>
                    </Box>
                )}
                {!editing && !contest.refund_policy && (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mt: 1.5 }}>
                        Defaulting to &ldquo;{DEFAULT_CONTEST_REFUND_POLICY}&rdquo; until you save a choice.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
